import React, { useCallback, useMemo, useState } from 'react';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Spinners from '../../../components/spinner';
import {
  DynamicTable,
  useDynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import BrokerTradeHistoryHeader from '../../../constants/table-headers/brokerTradeHistoryHeader';
import { axiosInstance } from '../../../services/apiInterceptor';
import { useBoolean, useGetCsTransactionHistory } from '../../../hooks';
import type { CsTransactionHistoryDto } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import type { FillTradeOrderDto } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import { CsBrokerTradeToolSettlementService } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import { ActionButton } from '../../../components/vertical-dot/popper';
import ViewDetailsModal from './viewDetailsModal';
import DoneTradeModal from './doneTradeModal';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import type { Wealthlane_AwardService_Dtos_Transactions_TransactionList } from '@/services/wealthlane-award-services';

interface DoneTradeSectionProps {
  participantId: string | undefined;
  selectedParticipant: any | null;
  companyId: string | undefined;
  settlementHandleRequest: (promise: any) => Promise<any>;
  awardHandleRequest: (promise: any) => Promise<any>;
  orderStatusOptions: any;
  isOrderStatusLoading: boolean;
  canFillDT: boolean;
}

function DoneTradeSection({
  participantId,
  selectedParticipant,
  companyId,
  settlementHandleRequest,
  awardHandleRequest,
  orderStatusOptions,
  isOrderStatusLoading,
  canFillDT,
}: DoneTradeSectionProps) {
  const queryClient = useQueryClient();
  const { tableInfo } = useDynamicTable();

  const {
    schemaData: historySchemaData,
    isTableSchemaLoading,
    status: schemaStatus,
  } = useTableSchemaLoader({
    identifier: 'CS_BROKER_DONE_TRADE_TABLE',
  });
  const hasHistorySchema = schemaStatus === 'success' && historySchemaData?.columns?.length > 0;

  const [viewDetailsRow, setViewDetailsRow] = useState<CsTransactionHistoryDto | null>(null);
  const {
    value: isViewDetailsOpen,
    setFalse: closeViewDetails,
    setTrue: openViewDetails,
  } = useBoolean(false);

  const [manualFillRow, setManualFillRow] =
    useState<Wealthlane_AwardService_Dtos_Transactions_TransactionList | null>(null);
  const {
    value: isManualFillOpen,
    setFalse: closeManualFill,
    setTrue: openManualFill,
  } = useBoolean(false);
  const [isManualFillSubmitting, setIsManualFillSubmitting] = useState(false);

  const historySorting = tableInfo?.sorting;
  const historySkipCount = tableInfo?.skipCount ?? 0;
  const historyMaxResult = tableInfo?.maxResultCount ?? 10;

  const pendingStatusId = useMemo(() => {
    const pending = orderStatusOptions?.find((opt: any) => opt.label?.toLowerCase() === 'pending');
    return pending?.value ?? '';
  }, [orderStatusOptions]);

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    status: historyStatus,
  } = useGetCsTransactionHistory(
    settlementHandleRequest,
    participantId,
    companyId,
    historySorting,
    historySkipCount,
    historyMaxResult,
    {
      recordTypes: tableInfo?.recordTypes,
      fromDate: tableInfo?.fromDate,
      toDate: tableInfo?.toDate,
      orderStatusId: pendingStatusId ? [pendingStatusId] : [],
    }
  );
  const historyItems = historyData?.items;
  const historyTotalCount = historyData?.totalCount ?? 0;

  const handleManualFillOpen = useCallback(
    (h: CsTransactionHistoryDto) => {
      setManualFillRow(h);
      openManualFill();
    },
    [openManualFill]
  );

  const handleManualFillSubmit = useCallback(
    async (fillData: any) => {
      if (!manualFillRow?.tradeOrderId) return;
      setIsManualFillSubmitting(true);
      try {
        await settlementHandleRequest(
          CsBrokerTradeToolSettlementService.putApiAwardSettlementApiAppCsBrokerTradeToolSettlementFill(
            manualFillRow.tradeOrderId as unknown as string,
            companyId,
            fillData
          )
        );
        toast.success(<ToastComponent label='Success' message='Order filled successfully.' />);
        void queryClient.invalidateQueries('getCsTransactionHistory');
        closeManualFill();
        setManualFillRow(null);
      } catch (err: any) {
        const msg = err?.body?.error?.message || err?.message || '';
        if (msg.includes('STALE_BALANCE')) {
          toast.error(
            <ToastComponent
              label='Error'
              message='Balance has changed. Refreshing available shares...'
            />
          );
          void queryClient.invalidateQueries('getCsSellItems');
        }
      } finally {
        setIsManualFillSubmitting(false);
      }
    },
    [manualFillRow, settlementHandleRequest, companyId, queryClient, closeManualFill]
  );

  const openViewDetailsForRow = useCallback(
    (row: CsTransactionHistoryDto) => {
      setViewDetailsRow(row);
      openViewDetails();
    },
    [openViewDetails]
  );

  const getHistoryActions = useCallback(
    (h: Wealthlane_AwardService_Dtos_Transactions_TransactionList) => {
      const actions: any[] = [
        {
          id: 'viewDetails',
          action: 'View Details',
          onClick: () => {
            openViewDetailsForRow(h);
          },
        },
      ];

      const statusLower = h.orderStatusSystemName?.toLowerCase() ?? '';
      const isPending = statusLower === 'pending';
      const isAlreadyVoidedOrCancelled =
        statusLower === 'cancelled' || statusLower === 'reversed' || statusLower === 'voided';

      if (
        isPending &&
        h.tradeOrderId &&
        !(h as any).orderNumber &&
        !isAlreadyVoidedOrCancelled &&
        canFillDT
      ) {
        actions.push({
          id: 'enterFill',
          action: 'Enter Fill',
          onClick: () => {
            handleManualFillOpen(h);
          },
        });
      }

      return actions;
    },
    [openViewDetailsForRow, handleManualFillOpen, canFillDT]
  );

  const historySchema = useMemo(
    () => ({
      columns: BrokerTradeHistoryHeader({
        actions: historySchemaData?.columns || [],
        getHistoryActions,
      }),
      options: historySchemaData?.options as any,
    }),
    [historySchemaData, getHistoryActions]
  );

  const historyTableData = useMemo(() => historyItems || [], [historyItems]);

  return (
    <>
      {isTableSchemaLoading ? (
        <Spinners id='doneTradeSchemaSpinner' />
      ) : !hasHistorySchema ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
          Table schema not available. Please seed the <strong>CS_BROKER_TRADE_HISTORY_TABLE</strong>{' '}
          schema in the Administration Service.
        </div>
      ) : (
        <DynamicTable
          id='doneTradeTable'
          schema={historySchema}
          data={historyTableData}
          totalCounts={historyTotalCount || 10}
          loading={historyStatus}
          showPagination={historyTotalCount > 0}
          showPaginationList={historyTotalCount > historyMaxResult}
          components={{}}
        />
      )}

      {/* View Details Modal */}
      <AnimationWrapper>
        {isViewDetailsOpen && (
          <ViewDetailsModal
            isOpen={isViewDetailsOpen}
            onClose={() => {
              closeViewDetails();
              setViewDetailsRow(null);
            }}
            row={viewDetailsRow}
            companyId={companyId || ''}
          />
        )}
      </AnimationWrapper>

      {/* Manual Fill Modal */}
      <AnimationWrapper>
        {isManualFillOpen && manualFillRow && (
          <DoneTradeModal
            isOpen={isManualFillOpen}
            onClose={() => {
              closeManualFill();
              setManualFillRow(null);
            }}
            previewData={
              {
                tradeOrderId: manualFillRow.tradeOrderId as unknown as string,
                transactionId: manualFillRow.transactionId as unknown as string,
                shares: manualFillRow.share ?? 0,
                company: selectedParticipant?.companyName ?? '',
                ticker: selectedParticipant?.ticker ?? '',
                brokerName: selectedParticipant?.brokerName ?? '',
                brokerAccount: selectedParticipant?.brokerAccountNumber ?? '',
                orderType: 'Market',
                createdDate: (manualFillRow as any).creationTime,
                brokerSellType: '',
                awardType: manualFillRow.awardType ?? '',
                recordType: manualFillRow.recordType ?? '',
                orderStatus: manualFillRow.transactionStatus ?? '',
              } as any
            }
            onSubmit={handleManualFillSubmit}
            isSubmitting={isManualFillSubmitting}
            orderStatusOptions={orderStatusOptions}
            isOrderStatusLoading={isOrderStatusLoading}
            flowMode='manual-fill'
          />
        )}
      </AnimationWrapper>
    </>
  );
}

export default withDynamicTableContext(DoneTradeSection, true, axiosInstance);
